import {
	MAX_ITEMS_PER_ROW,
	ROW_ID_PREFIX,
	TOTAL_NUMBER_OF_COLUMNS,
} from './constants';
import type { DnDItem, DnDLayoutItem } from './types';

/**
 * Calculates item span based on item count or provided span
 */
export const calculateItemSpan = (
	itemCount: number,
	providedSpan?: number
): number => {
	if (providedSpan !== undefined) {
		return providedSpan;
	}

	// When total number of columns is 12:
	// For 3 items: 12/3 = 4 columns each
	// For 2 items: 12/2 = 6 columns each
	// For 1 item: 12/1 = 12 columns
	return TOTAL_NUMBER_OF_COLUMNS / itemCount;
};

/**
 * Calculates spans for all items in a row
 */
export const calculateItemSpansForRow = <T>(
	items: DnDItem<T>[]
): Record<string, number> => {
	const itemSpans: Record<string, number> = {};
	const itemCount = items.length;

	items.forEach((item) => {
		const calculatedSpan = calculateItemSpan(itemCount);
		itemSpans[item.id] = calculatedSpan;
	});

	return itemSpans;
};

/**
 * Creates layout object from current state
 */
export const createLayout = <T>(
	rowIds: string[],
	itemsByRowId: Record<string, DnDItem<T>[]>,
	itemSpansById: Record<string, number>
): Record<string, DnDLayoutItem> => {
	const layout: Record<string, DnDLayoutItem> = {};

	rowIds.forEach((rowId, rowIndex) => {
		const rowItems = itemsByRowId[rowId] || [];
		rowItems.forEach((item, orderIndex) => {
			layout[item.id] = {
				row: rowIndex + 1,
				order: orderIndex + 1,
				span: itemSpansById[item.id],
			};
		});
	});

	return layout;
};

/**
 * Creates items lookup by ID
 */
export const createItemsById = <T>(
	items: DnDItem<T>[]
): Record<string, DnDItem<T>> =>
	items.reduce(
		(acc, item) => ({ ...acc, [item.id]: item }),
		{} as Record<string, DnDItem<T>>
	);

/**
 * Generates unique ID using crypto.randomUUID or fallback
 */
export const generateUniqueId = (): string => {
	// Try to use crypto.randomUUID() if available (modern browsers and Node.js 19+)
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return crypto.randomUUID();
	}

	// Fallback: use timestamp + random number for older environments
	const timestamp = Date.now().toString(36);
	const randomPart = Math.random().toString(36).substring(2, 15);
	return `${timestamp}-${randomPart}`;
};

/**
 * Creates items grouped by row ID from layout and returns both row IDs and grouped items.
 * Automatically adds missing items to new rows if they exist in items but not in layout.
 */
export const createItemsByRowId = <T>(
	items: DnDItem<T>[],
	layout: Record<string, DnDLayoutItem>
): {
	rowIds: string[];
	itemsByRowId: Record<string, DnDItem<T>[]>;
} => {
	const rowGroups: Record<string, DnDItem<T>[]> = {};

	// Initialize row groups with existing rows from layout
	const existingRows = new Set(
		Object.values(layout)
			.sort((a, b) => a.row - b.row)
			.map((item) => `${ROW_ID_PREFIX}-${item.row.toString()}`)
	);
	existingRows.forEach((rowId) => {
		rowGroups[rowId] = [];
	});

	// Process items that are in the layout
	Object.entries(layout).forEach(([itemId, layoutItem]) => {
		const rowKey = `${ROW_ID_PREFIX}-${layoutItem.row.toString()}`;
		const item = items.find((currentItem) => currentItem.id === itemId);
		if (item && rowGroups[rowKey]) {
			rowGroups[rowKey][layoutItem.order - 1] = item;
		}
	});

	// Clean up empty slots in existing rows
	Object.keys(rowGroups).forEach((rowKey) => {
		rowGroups[rowKey] = rowGroups[rowKey].filter(Boolean);
	});

	// Find items that are not in the layout
	const itemsInLayout = new Set(Object.keys(layout));
	const missingItems = items.filter((item) => !itemsInLayout.has(item.id));

	// Add missing items to new rows if it is missing from the layout
	if (missingItems.length > 0) {
		// Group missing items into rows based on MAX_ITEMS_PER_ROW
		const missingItemRows: DnDItem<T>[][] = [];
		for (let i = 0; i < missingItems.length; i += MAX_ITEMS_PER_ROW) {
			missingItemRows.push(missingItems.slice(i, i + MAX_ITEMS_PER_ROW));
		}

		// Add each row of missing items
		missingItemRows.forEach((rowItems) => {
			const newRowId = generateUniqueId();
			rowGroups[newRowId] = rowItems;
		});
	}

	// Return both row IDs and grouped items
	return {
		rowIds: Object.keys(rowGroups),
		itemsByRowId: rowGroups,
	};
};

/**
 * Validates and normalizes a span value
 */
const validateSpan = (span: number | undefined): number | null => {
	if (span === undefined || span === null) {
		return null;
	}

	// Check if span is within valid range (1 to TOTAL_NUMBER_OF_COLUMNS)
	if (span >= 1 && span <= TOTAL_NUMBER_OF_COLUMNS) {
		return span;
	}

	// Invalid span value
	return null;
};

/**
 * Creates item spans lookup by ID
 */
export const createItemSpansById = <T>(
	_items: DnDItem<T>[],
	layout: Record<string, DnDLayoutItem>,
	itemsByRowId: Record<string, DnDItem<T>[]>
): Record<string, number> => {
	const itemSpansById: Record<string, number> = {};

	Object.values(itemsByRowId).forEach((rowItems) => {
		const rowItemSpans = calculateItemSpansForRow(rowItems);

		// Use span from layout if valid, otherwise use calculated span
		rowItems.forEach((item) => {
			const layoutItem = layout[item.id];
			const validatedSpan = validateSpan(layoutItem?.span);
			itemSpansById[item.id] = validatedSpan ?? rowItemSpans[item.id];
		});
	});

	return itemSpansById;
};

/**
 * Extracts item ID from indicator ID
 */
export const extractItemIdFromIndicator = (
	indicatorId: string
): string | null => {
	if (indicatorId.startsWith('dnd-vertical-indicator-before-')) {
		return indicatorId.replace('dnd-vertical-indicator-before-', '');
	} else if (indicatorId.startsWith('dnd-vertical-indicator-after-')) {
		return indicatorId.replace('dnd-vertical-indicator-after-', '');
	} else if (indicatorId.startsWith('dnd-horizontal-indicator-above-')) {
		return indicatorId.replace('dnd-horizontal-indicator-above-', '');
	} else if (indicatorId.startsWith('dnd-horizontal-indicator-below-')) {
		return indicatorId.replace('dnd-horizontal-indicator-below-', '');
	}
	return null;
};

/**
 * Finds row ID for a given indicator ID
 */
export const findRowForIndicator = <T>(
	indicatorId: string,
	itemsByRowId: Record<string, DnDItem<T>[]>
): string | null => {
	const itemId = extractItemIdFromIndicator(indicatorId);
	if (!itemId) {
		return null;
	}

	// Find which row contains this item
	return (
		Object.keys(itemsByRowId).find((rowId) =>
			itemsByRowId[rowId].some((item) => item.id === itemId)
		) || null
	);
};
